import { Component, OnInit } from '@angular/core';
import { CdkDragStart } from '@angular/cdk/drag-drop';

export interface FieldType {
	type: string;
	label: string;
	icon: string;
	description: string;
	category: string;
}

export interface FieldCategory {
	name: string;
	label: string;
	icon: string;
	expanded: boolean;
	fields: FieldType[];
}

@Component({
	selector: 'nx-ahs-field-palette',
	templateUrl: './field-palette.component.html',
	styleUrl: './field-palette.component.scss',
	standalone: false
})
export class FieldPaletteComponent implements OnInit {
	searchTerm: string = '';
	categories: FieldCategory[] = [];

	private allFieldTypes: FieldType[] = [
		// Basic Input Fields
		{
			type: 'input',
			label: 'Text Input',
			icon: 'text_fields',
			description: 'Single-line text input',
			category: 'basic'
		},
		{
			type: 'textarea',
			label: 'Text Area',
			icon: 'notes',
			description: 'Multi-line text input',
			category: 'basic'
		},
		{
			type: 'select',
			label: 'Select',
			icon: 'arrow_drop_down_circle',
			description: 'Dropdown selection',
			category: 'basic'
		},
		{ type: 'checkbox', label: 'Checkbox', icon: 'check_box', description: 'Checkbox field(s)', category: 'basic' },
		{
			type: 'radio',
			label: 'Radio',
			icon: 'radio_button_checked',
			description: 'Radio button group',
			category: 'basic'
		},
		{ type: 'date', label: 'Date', icon: 'event', description: 'Date picker', category: 'basic' },

		// Display & Static
		{
			type: 'static',
			label: 'Static Text',
			icon: 'description',
			description: 'Display-only text/HTML',
			category: 'display'
		},
		{
			type: 'hidden',
			label: 'Hidden Field',
			icon: 'visibility_off',
			description: 'Hidden field with fixed/API values',
			category: 'display'
		},

		// Advanced Input
		{
			type: 'richtext',
			label: 'Rich Text',
			icon: 'format_color_text',
			description: 'Rich text editor',
			category: 'advanced'
		},
		{ type: 'chips', label: 'Chips', icon: 'label', description: 'Chip input field', category: 'advanced' },
		{
			type: 'chips-autocomplete',
			label: 'Chips Autocomplete',
			icon: 'local_offer',
			description: 'Chips with autocomplete',
			category: 'advanced'
		},
		{
			type: 'address',
			label: 'Address',
			icon: 'location_on',
			description: 'Complete address form with maps',
			category: 'advanced'
		},
		{
			type: 'file-upload',
			label: 'File Upload',
			icon: 'cloud_upload',
			description: 'File upload field',
			category: 'advanced'
		},

		// Layout & Structure
		{
			type: 'group',
			label: 'Group',
			icon: 'view_agenda',
			description: 'Container for grouping fields',
			category: 'layout'
		},
		{
			type: 'repeater',
			label: 'Repeater',
			icon: 'repeat',
			description: 'Dynamic array of field groups',
			category: 'layout'
		},
		{
			type: 'nested-script',
			label: 'Nested Script',
			icon: 'list_alt',
			description: 'Embed another Formly script',
			category: 'layout'
		},

		// Special
		{
			type: 'setter',
			label: 'Setter',
			icon: 'settings',
			description: 'Sets values from API/variables',
			category: 'special'
		}
	];

	ngOnInit(): void {
		this.initializeCategories();
	}

	private initializeCategories(): void {
		this.categories = [
			{
				name: 'basic',
				label: 'Basic Fields',
				icon: 'widgets',
				expanded: true,
				fields: this.allFieldTypes.filter((f) => f.category === 'basic')
			},
			{
				name: 'display',
				label: 'Display & Static',
				icon: 'visibility',
				expanded: false,
				fields: this.allFieldTypes.filter((f) => f.category === 'display')
			},
			{
				name: 'advanced',
				label: 'Advanced Fields',
				icon: 'tune',
				expanded: false,
				fields: this.allFieldTypes.filter((f) => f.category === 'advanced')
			},
			{
				name: 'layout',
				label: 'Layout & Structure',
				icon: 'dashboard',
				expanded: false,
				fields: this.allFieldTypes.filter((f) => f.category === 'layout')
			},
			{
				name: 'special',
				label: 'Special',
				icon: 'star',
				expanded: false,
				fields: this.allFieldTypes.filter((f) => f.category === 'special')
			}
		];
	}

	get filteredCategories(): FieldCategory[] {
		if (!this.searchTerm) {
			return this.categories;
		}

		const term = this.searchTerm.toLowerCase();
		return this.categories
			.map((category) => ({
				...category,
				expanded: true,
				fields: category.fields.filter(
					(field) =>
						field.label.toLowerCase().includes(term) ||
						field.type.toLowerCase().includes(term) ||
						field.description.toLowerCase().includes(term)
				)
			}))
			.filter((category) => category.fields.length > 0);
	}

	toggleCategory(category: FieldCategory): void {
		category.expanded = !category.expanded;
	}

	onDragStarted(event: CdkDragStart, fieldType: FieldType): void {
		// Store field type for drag operation
		event.source.data = fieldType;
	}

	clearSearch(): void {
		this.searchTerm = '';
	}
}
